import type { DefaultTheme } from 'styled-components';
import { ThemeProvider } from 'styled-components';
import type { COLOR_SCHEME, ChangeColorPreference } from '~/hooks/useColorPreference';
import { useColorPreference } from '~/hooks/useColorPreference';
import type { ReactNode, JSX } from 'react';
import { createContext, useContext, useMemo } from 'react';
import type { Nullable } from '~/utilities/type-guards';
import { isNil, isNotNil } from '~/utilities/type-guards';
import { useColorThemes } from './hooks/useColorThemes';

interface ThemeContextProviderReturnType {
  colorScheme: COLOR_SCHEME;
  changeColorPreference: ChangeColorPreference;
}

const ThemeProviderContext = createContext<Nullable<ThemeContextProviderReturnType>>(null);
const ForcedSchemeProviderContext = createContext<Nullable<COLOR_SCHEME>>(null);

export const useThemeContext = (): ThemeContextProviderReturnType => {
  const context = useContext(ThemeProviderContext);
  if (isNil(context)) {
    throw new Error('useThemeContext must be called inside of a context provider');
  }
  return context;
};

export const ForcedColorSchemeProvider = ({
  color,
  children,
}: {
  color: COLOR_SCHEME;
  children;
}): JSX.Element => {
  return (
    <ForcedSchemeProviderContext.Provider value={color}>
      {children}
    </ForcedSchemeProviderContext.Provider>
  );
};

const useForcedColorScheme = (): Nullable<COLOR_SCHEME> => {
  return useContext(ForcedSchemeProviderContext);
};

export const ThemeContextProvider = ({
  children,
}: {
  children: ReactNode | ReactNode[];
}): JSX.Element => {
  const [scheme, changeColorPreference] = useColorPreference();
  const { pathname } = window.location;
  const isNotRecordPath = !pathname.includes('record/new');
  const forcedScheme = useForcedColorScheme();

  const useColorPreferenceValue = useMemo(() => {
    return {
      colorScheme: isNotRecordPath ? 'light' : scheme,
      changeColorPreference,
    };
  }, [isNotRecordPath, scheme, changeColorPreference]);

  const [lightMode, darkMode] = useColorThemes();

  let themeMode = scheme === 'light' || isNotRecordPath ? lightMode : darkMode;

  if (isNotNil(forcedScheme)) {
    themeMode = forcedScheme === 'light' ? lightMode : darkMode;
  }

  return (
    <ThemeProviderContext.Provider value={useColorPreferenceValue}>
      <ThemeProvider theme={themeMode as DefaultTheme}>{children}</ThemeProvider>
    </ThemeProviderContext.Provider>
  );
};
